"use client";

import { OtherGames } from "@/components/ui/games/OtherGames";
import { FetchButton } from "@/fetch-ui/FetchButton";
import FetchModal from "@/fetch-ui/FetchModal";
import JSConfetti from "js-confetti";
import { HelpCircle, Timer } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { WordleBoard } from "./_components/WordleBoard";
import { WordleKeyboard } from "./_components/WordleKeyboard";
import { WordleLeaderboard } from "./_components/WordleLeaderboard";
import { WordleResultCard } from "./_components/WordleResultCard";
import { WordleRules } from "./_components/WordleRules";
import { WordleStartCard } from "./_components/WordleStartCard";
import styles from "./_components/wordle.module.css";
import { useLeaderboard } from "./_hooks/useLeaderboard";
import { useWordleRound } from "./_hooks/useWordleRound";
import { MAX_GUESSES } from "./_logic/constants";
import { formatDuration, playerKey } from "./_logic/scoring";

function WordlePageContent() {
  // Hooks
  const searchParams = useSearchParams();
  const game = useWordleRound();
  const leaderboard = useLeaderboard(game.state);

  // States
  const [rulesOpen, setRulesOpen] = useState(false);

  // Derived values
  const isClubhouse = searchParams?.get("id") === "clubhouse";
  const round = game.round;
  const finished = Boolean(round) && game.state !== "playing";
  const standing =
    round && finished ? (leaderboard.entries.find((e) => playerKey(e.name) === playerKey(round.name)) ?? null) : null;
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  // Effects
  useEffect(() => {
    if (game.state !== "won" || !round?.counted) return;
    const id = setTimeout(() => new JSConfetti().addConfetti(), 900);
    return () => clearTimeout(id);
  }, [game.state, round?.counted]);

  // Render
  return (
    <main className="flex min-h-screen justify-center bg-slate-50 px-4 pt-24 pb-16 shadow-[0_0_0_100vmax_#f8fafc] [clip-path:inset(0_-100vmax)]">
      <div className="flex w-full max-w-5xl flex-col gap-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            {isClubhouse && (
              <Image src="/landing/clubhouse.png" alt="Clubhouse" width={160} height={80} className="h-16 w-auto" />
            )}
            <p className="text-xs font-semibold tracking-wide text-indigo-600 uppercase">Nowadays games</p>
            <h1 className="text-3xl font-bold text-slate-900 md:text-4xl">Wordle</h1>
            <p className="text-sm font-medium text-slate-500">{today}</p>
          </div>
          <FetchButton variant="ghost" leftIcon={<HelpCircle className="h-4 w-4" />} onClick={() => setRulesOpen(true)}>
            How to play
          </FetchButton>
        </header>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
          <section className="flex flex-col gap-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
            {!game.hydrated ? (
              <div className="h-80 animate-pulse rounded-xl bg-slate-100" />
            ) : !round ? (
              <WordleStartCard
                name={game.name}
                onNameChange={game.setName}
                onStart={game.start}
                starting={game.starting}
              />
            ) : (
              <div className="mx-auto flex w-full max-w-[22rem] flex-col gap-5">
                <div className="flex items-center justify-between text-sm font-medium text-slate-600">
                  <span>
                    Playing as <span className="font-semibold text-slate-900">{round.name}</span>
                  </span>
                  <span className="flex items-center gap-3 tabular-nums">
                    <span>
                      {round.guesses.length}/{MAX_GUESSES}
                    </span>
                    <span className="flex items-center gap-1">
                      <Timer className="h-4 w-4 text-slate-400" />
                      {formatDuration(game.elapsed)}
                    </span>
                  </span>
                </div>

                <div className="relative">
                  {game.toast && (
                    <div
                      role="status"
                      className={`absolute top-2 left-1/2 z-10 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold whitespace-nowrap text-white shadow-lg ${styles.fadeIn}`}
                    >
                      {game.toast}
                    </div>
                  )}
                  <WordleBoard
                    guesses={game.guesses}
                    currentGuess={game.currentGuess}
                    state={game.state}
                    shakeKey={game.shakeKey}
                  />
                </div>

                {finished ? (
                  <WordleResultCard round={round} standing={standing} onPlayAsSomeoneElse={game.restartAsSomeoneElse} />
                ) : (
                  <WordleKeyboard guesses={game.guesses} onKey={game.pressKey} disabled={game.submitting} />
                )}
              </div>
            )}

            {!round && game.toast && (
              <p role="status" className="text-sm font-medium text-red-600">
                {game.toast}
              </p>
            )}
          </section>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-24">
            <WordleLeaderboard
              entries={leaderboard.entries}
              loading={leaderboard.loading}
              error={leaderboard.error}
              playerName={round?.name ?? null}
            />
          </aside>
        </div>

        {finished && <OtherGames currentGameId="wordle" />}
      </div>

      <FetchModal isOpen={rulesOpen} onClose={() => setRulesOpen(false)} title="How to play">
        <WordleRules />
      </FetchModal>
    </main>
  );
}

/**
 * Daily Wordle with server-side scoring and a live leaderboard.
 * @returns The page.
 */
export default function WordlePage() {
  return (
    <Suspense
      fallback={
        <main className="min-h-screen bg-slate-50 shadow-[0_0_0_100vmax_#f8fafc] [clip-path:inset(0_-100vmax)]" />
      }
    >
      <WordlePageContent />
    </Suspense>
  );
}
